import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useQueries } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import {
  getSupervisorMetrics,
  getSupervisorStorePerformance,
  getSupervisorAlerts,
  getSupervisorDailySales,
  getSupervisorTopSellers,
} from "@/lib/api/supervisor-api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { setCurrentShop, setCurrentUser, getCurrentUser } from "@/lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from "recharts";
import {
  DollarSign, Receipt, ShoppingCart, Store, AlertTriangle, TrendingUp, TrendingDown,
  Bell, Plus, Users, FileText, LogOut, Building, Search, ArrowRight, Package, User,
  Clock, AlertCircle,
} from "lucide-react";

function formatCurrency(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function TrendBadge({ value }: { value: number }) {
  if (value === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const isUp = value > 0;
  return (
    <span className={`text-xs font-medium inline-flex items-center gap-0.5 ${isUp ? "text-green-600" : "text-red-600"}`}>
      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isUp ? "+" : ""}{value}%
    </span>
  );
}

export default function SupervisorDashboard() {
  const [, setLocation] = useLocation();
  const currentUser = getCurrentUser();

  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  const [searchTerm, setSearchTerm] = useState("");

  const dateParams = { startDate, endDate };

  const queries = useQueries({
    queries: [
      {
        queryKey: ["supervisor-metrics", startDate, endDate],
        queryFn: () => getSupervisorMetrics("", startDate, endDate),
      },
      {
        queryKey: ["supervisor-store-performance", startDate, endDate],
        queryFn: () => getSupervisorStorePerformance("", startDate, endDate),
      },
      {
        queryKey: ["supervisor-alerts"],
        queryFn: () => getSupervisorAlerts(),
      },
      {
        queryKey: ["supervisor-daily-sales", startDate, endDate],
        queryFn: () => getSupervisorDailySales("", startDate, endDate),
      },
      {
        queryKey: ["supervisor-top-sellers", startDate, endDate],
        queryFn: () => getSupervisorTopSellers("", startDate, endDate),
      },
    ],
  });

  const [metricsData, storeData, alertsData, dailySalesData, topSellersData] = queries;

  const metrics = metricsData.data;
  const { stores = [] } = storeData.data || {};
  const { alerts = [] } = alertsData.data || {};
  const { dailySales = [] } = dailySalesData.data || {};
  const { sellers = [] } = topSellersData.data || {};

  const isLoading = queries.some(q => q.isLoading);

  const filteredStores = useMemo(() => {
    if (!searchTerm) return stores;
    const q = searchTerm.toLowerCase();
    return stores.filter((s: any) => s.name.toLowerCase().includes(q));
  }, [stores, searchTerm]);

  const dailyChartData = useMemo(() => {
    const grouped: Record<string, any> = {};
    for (const d of dailySales) {
      if (!grouped[d.date]) grouped[d.date] = { date: d.date };
      grouped[d.date][d.shopName] = (grouped[d.date][d.shopName] || 0) + d.sales;
    }
    return Object.values(grouped);
  }, [dailySales]);

  const chartShops = useMemo(() => {
    const s = new Set(dailySales.map(d => d.shopName));
    return Array.from(s);
  }, [dailySales]);

  const colors = ["#2563eb", "#16a34a", "#d97706", "#dc2626", "#8b5cf6", "#0891b2", "#be185d", "#65a30d"];

  const [showUserManager, setShowUserManager] = useState(false);
  const [showReportGeneral, setShowReportGeneral] = useState(false);
  const [showInventoryGlobal, setShowInventoryGlobal] = useState(false);

  const enterShop = (shop: any) => {
    setCurrentShop(shop);
    setCurrentUser({
      id: currentUser?.id || "",
      shopId: shop.id,
      role: "supervisor",
      fullName: currentUser?.fullName || "Supervisor",
    });
    setLocation(`/shop/dashboard`);
  };

  const enterShopForUsers = (shop: any) => {
    setCurrentShop(shop);
    setCurrentUser({
      id: currentUser?.id || "",
      shopId: shop.id,
      role: "supervisor",
      fullName: currentUser?.fullName || "Supervisor",
    });
    setShowUserManager(false);
    setLocation(`/shop/users`);
  };

  const enterShopForReports = (shop: any) => {
    setCurrentShop(shop);
    setCurrentUser({
      id: currentUser?.id || "",
      shopId: shop.id,
      role: "supervisor",
      fullName: currentUser?.fullName || "Supervisor",
    });
    setShowReportGeneral(false);
    setLocation(`/shop/reports`);
  };

  const enterShopForInventory = (shop: any) => {
    setCurrentShop(shop);
    setCurrentUser({
      id: currentUser?.id || "",
      shopId: shop.id,
      role: "supervisor",
      fullName: currentUser?.fullName || "Supervisor",
    });
    setShowInventoryGlobal(false);
    setLocation(`/shop/inventory`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg hidden sm:inline">QvaPOS</span>
            </div>

            <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
              <span>
                Hola, <strong className="text-foreground">{currentUser?.fullName || "Supervisor"}</strong>
                <Badge variant="secondary" className="ml-1 text-xs">Supervisor</Badge>
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                {(currentUser?.fullName || "S").charAt(0)}
              </div>
              <Button variant="ghost" size="sm" onClick={() => { localStorage.clear(); setLocation("/shop/login"); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Date Range Selector */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-bold">Panel de Supervisión</h1>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <label className="text-sm text-muted-foreground hidden sm:inline">Desde:</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full sm:w-40" />
              <label className="text-sm text-muted-foreground hidden sm:inline">Hasta:</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full sm:w-40" />
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <Card>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Ventas</p>
                    <p className="text-lg sm:text-2xl font-bold mt-1">{isLoading ? "..." : formatCurrency(metrics?.totalSales || 0)}</p>
                  {metrics && <TrendBadge value={metrics.salesChange} />}
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Tickets</p>
                  <p className="text-lg sm:text-2xl font-bold mt-1">{isLoading ? "..." : formatNumber(metrics?.totalTickets || 0)}</p>
                  {metrics && <TrendBadge value={metrics.ticketsChange} />}
                </div>
                <div className="p-2 bg-green-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Ticket Promedio</p>
                  <p className="text-lg sm:text-2xl font-bold mt-1">{isLoading ? "..." : formatCurrency(metrics?.averageTicket || 0)}</p>
                  {metrics && (
                    <span className="text-xs text-muted-foreground">
                      por transacción
                    </span>
                  )}
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Tiendas Activas</p>
                  <p className="text-lg sm:text-2xl font-bold mt-1">{isLoading ? "..." : `${metrics?.activeShops || 0} de ${metrics?.totalShops || 0}`}</p>
                  {metrics && (
                    <span className="text-xs text-green-600 font-medium inline-flex items-center gap-0.5">
                      <TrendingUp className="h-3 w-3" /> 100% operativas
                    </span>
                  )}
                </div>
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Store className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Store Performance Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle>Rendimiento por Tienda</CardTitle>
                <CardDescription>Ventas y tendencia por punto de venta</CardDescription>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tienda..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 w-full sm:w-60"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            <div className="overflow-x-auto">
              <div className="min-w-[650px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tienda</TableHead>
                      <TableHead>Gerente</TableHead>
                      <TableHead className="text-right">Ventas</TableHead>
                      <TableHead className="text-right">Tickets</TableHead>
                      <TableHead className="text-right">Ticket Prom</TableHead>
                      <TableHead className="text-right">Tendencia</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Cargando...</TableCell>
                      </TableRow>
                    ) : filteredStores.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          {searchTerm ? "No se encontraron tiendas" : "No hay tiendas disponibles"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStores.map((store: any) => (
                        <TableRow key={store.id}>
                          <TableCell className="font-medium">{store.name}</TableCell>
                          <TableCell>{store.managerName || <span className="text-amber-600 text-xs">Vacante</span>}</TableCell>
                          <TableCell className="text-right">{formatCurrency(store.sales)}</TableCell>
                          <TableCell className="text-right">{formatNumber(store.tickets)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(store.averageTicket)}</TableCell>
                          <TableCell className="text-right">
                            {store.trend !== 0 ? <TrendBadge value={store.trend} /> : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => enterShop(store)}>
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts + Charts Row */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Alerts */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Alertas
                </CardTitle>
                <Badge variant="outline" className="text-xs">{alerts.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 max-h-60 sm:max-h-80 overflow-y-auto">
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Cargando...</p>
              ) : alerts.length === 0 ? (
                <div className="text-center py-6">
                  <AlertCircle className="h-8 w-8 mx-auto text-green-400 mb-2" />
                  <p className="text-sm text-muted-foreground">Sin alertas activas</p>
                </div>
              ) : (
                alerts.map((a: any, i: number) => (
                  <div key={i} className={`p-3 rounded-lg border text-sm ${
                    a.type === "critical" ? "border-red-200 bg-red-50" :
                    a.type === "warning" ? "border-amber-200 bg-amber-50" :
                    "border-blue-200 bg-blue-50"
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${
                        a.type === "critical" ? "bg-red-500" :
                        a.type === "warning" ? "bg-amber-500" : "bg-blue-500"
                      }`} />
                      <span className="font-medium">{a.shopName}</span>
                    </div>
                    <p className="text-muted-foreground ml-4">{a.message}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Daily Sales Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle>Ventas Diarias</CardTitle>
              <CardDescription>Últimos {dailyChartData.length} días</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-52 sm:h-64 flex items-center justify-center text-muted-foreground">Cargando...</div>
              ) : dailyChartData.length === 0 ? (
                <div className="h-52 sm:h-64 flex items-center justify-center text-muted-foreground">Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    {chartShops.map((name, i) => (
                      <Bar key={name} dataKey={name} fill={colors[i % colors.length]} stackId="a" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
              {chartShops.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3 justify-center">
                  {chartShops.map((name, i) => (
                    <span key={name} className="text-xs inline-flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: colors[i % colors.length] }} />
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Sellers + Quick Actions */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Top Sellers */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Ranking de Vendedores
              </CardTitle>
              <CardDescription>Top vendedores del período</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-center py-6 text-muted-foreground">Cargando...</p>
              ) : sellers.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground">Sin datos de vendedores</p>
              ) : (
                <div className="space-y-3">
                  {sellers.map((s: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? "bg-yellow-100 text-yellow-700" :
                          i === 1 ? "bg-gray-200 text-gray-600" :
                          i === 2 ? "bg-orange-100 text-orange-700" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium">{s.fullName}</p>
                          <p className="text-xs text-muted-foreground">{s.shopName} &middot; {formatNumber(s.tickets)} tickets</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold">{formatCurrency(s.totalSales)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => setLocation("/owner/create-shop?return=/supervisor/dashboard")}>
                <Plus className="h-5 w-5 text-green-600" />
                <span>Nueva Tienda</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => setShowUserManager(true)}>
                <Users className="h-5 w-5 text-blue-600" />
                <span>Gestionar Gerentes</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => setShowReportGeneral(true)}>
                <FileText className="h-5 w-5 text-purple-600" />
                <span>Reporte General</span>
              </Button>
              <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={() => setShowInventoryGlobal(true)}>
                <Package className="h-5 w-5 text-orange-600" />
                <span>Inventario Global</span>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={showUserManager} onOpenChange={setShowUserManager}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seleccionar Tienda</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Elige una tienda para gestionar sus usuarios y gerentes:</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {stores.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay tiendas disponibles</p>
            ) : (
              stores.map((store: any) => (
                <Button
                  key={store.id}
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => enterShopForUsers(store)}
                >
                  <Store className="h-5 w-5 text-primary shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium truncate">{store.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{store.managerName || "Sin gerente asignado"}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto shrink-0 text-muted-foreground" />
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReportGeneral} onOpenChange={setShowReportGeneral}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seleccionar Tienda</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Elige una tienda para ver su reporte general:</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {stores.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay tiendas disponibles</p>
            ) : (
              stores.map((store: any) => (
                <Button
                  key={store.id}
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => enterShopForReports(store)}
                >
                  <Store className="h-5 w-5 text-primary shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium truncate">{store.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{store.managerName || "Sin gerente asignado"}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto shrink-0 text-muted-foreground" />
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showInventoryGlobal} onOpenChange={setShowInventoryGlobal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seleccionar Tienda</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Elige una tienda para ver su inventario:</p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {stores.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay tiendas disponibles</p>
            ) : (
              stores.map((store: any) => (
                <Button
                  key={store.id}
                  variant="outline"
                  className="w-full justify-start gap-3 h-14"
                  onClick={() => enterShopForInventory(store)}
                >
                  <Store className="h-5 w-5 text-primary shrink-0" />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium truncate">{store.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{store.managerName || "Sin gerente asignado"}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 ml-auto shrink-0 text-muted-foreground" />
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
